import { Component, Output, OnInit, OnDestroy, Injector, ViewChild, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';

// components
import { DxFormComponent, DxPopupComponent } from 'devextreme-angular';
import { ItemDetailsComponent } from '../../shared/components/item-details/item-details.component';

// models
import { DataFormat } from '../shared/models/product-property-types';
import { Language } from '../../shared/models/language';
import { MasterDataListField, FieldType } from '../../shared/models/master-data';
import { ProductPropertyType } from '../../shared/models/product';
import { ProductPropertyTypeEnum } from '../../shared/enum/productPropertyTypeEnum';

// services
import { LanguageService } from '../../shared/services/language.service';

import { ProductPropertyTypeService } from '../shared/services/product-property-type.service';


@Component({
  selector: 'master-data-field-component',
  templateUrl: 'master-data-field.component.html',
  styleUrls: ['./master-data-field.component.scss']
})
export class MasterDataFieldComponent extends ItemDetailsComponent<MasterDataListField> implements OnInit, OnDestroy {

  languages: Array<Language> = [];
  productPropertyTypes: Array<ProductPropertyType> = [];
  dataFormats: Array<DataFormat> = [];
  fieldTypes: Array<FieldType> = [];
  validFields: boolean = false;
  availableFieldsForCaption: Array<MasterDataListField> = [];

  @ViewChild('masterDataFieldName') masterDataFieldName;
  @ViewChild('masterDataFieldDescription') masterDataFieldDescription;
  @ViewChild(DxPopupComponent) popup: DxPopupComponent;
  @ViewChild('masterDataFieldForm', { read: DxFormComponent}) masterDataFieldForm: DxFormComponent;


  // tslint:disable:no-output-on-prefix
  @Output() onAdded = new EventEmitter<MasterDataListField>();
  @Output() onUpdated = new EventEmitter<MasterDataListField>();
  // tslint:enable:no-output-on-prefix

  rtlEnabled = localStorage.getItem('last-selected-language-direction') ? JSON.parse(localStorage.getItem('last-selected-language-direction')) : false;
  private _subscription: Subscription;

  constructor(
    protected injector: Injector,
    private languageService: LanguageService,
    private dataFormatsService: ProductPropertyTypeService,
  ) {
    super(injector);
    this._subscription = this.languageService.direction.subscribe(dir => {
      this.rtlEnabled = dir;
    });
  }

  ngOnInit() {
    this.model = new MasterDataListField();
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  open(masterDataListFields: Array<MasterDataListField>, masterDataListField: MasterDataListField, languages: Array<Language>, productPropertyTypes: Array<ProductPropertyType>, fieldTypes: Array<FieldType>) { // tslint:disable-line:max-line-length

    this.allItems = masterDataListFields;
    this.availableFieldsForCaption = masterDataListFields.filter(_ => _.masterDataListFieldId !== masterDataListField.masterDataListFieldId);
    this.translateMasterDataFields();

    this.languages = languages;
    this.fieldTypes = fieldTypes;
    this.productPropertyTypes = productPropertyTypes.filter(f => !f.isMasterData);

    if (masterDataListField.name !== null && masterDataListField.name !== undefined) {
      this.isEditMode = true;
      this.model = masterDataListField;

      this.validFields = this.languageService.checkDefaultLanguageNotEmpty(this.languages, this.model.name);

      this.filterDataFormats();
      this.isOpened = true;
    } else {
      this.model = masterDataListField;
      this.isEditMode = false;
      this.isOpened = true;

      // Create empty JSON object for translation fields
      const emptyTranslation = {};
      this.languages.forEach(lang => {
        emptyTranslation[lang.code] = '';
      });

      this.model.name = this.model.description = JSON.stringify(emptyTranslation);
    }
  }

  getTranslation(value: string) {
    return this.languageService.getTranslatableText(value);
  }

  save() {
    this.model.name = this.masterDataFieldName.data;
    this.model.description = this.masterDataFieldDescription.data;

    if (this.isEditMode) {
      this.onUpdated.emit(this.model);
    } else {
      this.onAdded.emit(this.model);
    }

    this.model = new MasterDataListField();
    // this.detailsForm.reset();
    this.close(true);
    this.errorMessage = null;
  }

  filterDataFormats() {
    if (this.model.propertyTypeId === ProductPropertyTypeEnum.DATE || this.model.propertyTypeId === ProductPropertyTypeEnum.DECIMAL
      || this.model.propertyTypeId === ProductPropertyTypeEnum.URL) {
      this.dataFormatsService.getFormats(this.model.propertyTypeId).subscribe(res => {
        this.dataFormats = res;
      });
    }
  }

  onTypeChanged = (e: any) => {
    if (this.model.propertyTypeId === ProductPropertyTypeEnum.DATE || this.model.propertyTypeId === ProductPropertyTypeEnum.DECIMAL
      || this.model.propertyTypeId === ProductPropertyTypeEnum.URL) {
      this.dataFormatsService.getFormats(this.model.propertyTypeId).subscribe(res => {
        this.dataFormats = res;
      });
    }
    if (this.model.propertyTypeId && this.model.propertyTypeId !== e.selectedItem.propertyTypeId) {
      this.model.propertyTypeFormatId = null;
      this.model.imageCaptionId = null;
    }
  }

  onUrlFormatChanged = (e: any) => {
    if (this.model.propertyTypeFormatId && this.model.propertyTypeFormatId !== e.selectedItem.propertyTypeFormatId) {
      this.model.imageCaptionId = null;
    }
  }

  translateMasterDataFields() {
    this.availableFieldsForCaption.forEach(masterDataListField => {
      masterDataListField.masterDataFieldName = this.languageService.getTranslatableText(masterDataListField.name);
      masterDataListField.masterDataDescription = this.languageService.getTranslatableText(masterDataListField.description);
    });
  }

  onChangeTranslationField = (e: any, fieldName: string) => {
    if (fieldName === 'name') {
      this.validFields = this.languageService.checkDefaultLanguageNotEmpty(this.languages, this.masterDataFieldName.data);
      this.masterDataFieldForm.instance.updateData(fieldName, this.masterDataFieldName.data);
    } else if (fieldName === 'description') {
      this.masterDataFieldForm.instance.updateData(fieldName, this.masterDataFieldDescription.data);
    }
  }

  onFieldDataChanged(e: any) {
    if ((e.component._isReady && e.component.NAME !== 'dxPopup') || e.component.NAME === 'dxForm') {
      const result = e.component.validate();
      if (result.brokenRules.length == 0 || this.validFields) {
        document.getElementsByName('btnMasterDataFieldSubmit')[0].removeAttribute('disabled');
      } else {
        document.getElementsByName('btnMasterDataFieldSubmit')[0].setAttribute('disabled', 'disabled');
      }
    } else {
      if (this.isEditMode && this.validFields) {
        document.getElementsByName('btnMasterDataFieldSubmit')[0].removeAttribute('disabled');
      } else {
        document.getElementsByName('btnMasterDataFieldSubmit')[0].setAttribute('disabled', 'disabled');
      }
    }
  }

}
